import breakpoints from '../../../breakpoints.json'
import { ariaIs, ariaSet } from '@vo/js/utils/aria'

const STORAGE_KEY = 'compare-selected'
const CATEGORY_KEY = 'compare-category'

class ProductCompare {
    static popup = document.querySelector('[data-compare-popup]')
    static inputs = document.querySelector('[data-compare-form-inputs]')
    static checkboxes = []
    static mq = window.matchMedia(`(min-width: ${breakpoints.lg}px)`)
    static limit = 9
    static selected = new Set()
    static listenersAdded = false

    static init () {
        this.restoreState()
        this.checkCategoryReset()
        if (this.popup) {
            if (!this.listenersAdded) {
                this.addCheckboxDelegation()
                this.addMediaQueryListeners()
                this.addLivewireListeners()
                this.listenersAdded = true
            }
            this.update()
        }
        this.initComparePage()
    }

    static update () {
        this.getCheckboxes()
        this.syncCheckboxesFromState()
        this.emptyForm()
        this.addInputs()
        this.togglePopup()
        this.popupMessage()
        this.disableCheckboxes()
        this.disableSubmitButton()
    }

    static getCheckboxes () {
        this.checkboxes = document.querySelectorAll('[data-compare-checkbox]')
    }

    static addCheckboxDelegation () {
        document.addEventListener('change', (e) => {
            const checkbox = e.target.closest('[data-compare-checkbox]')
            if (checkbox) {
                this.onCheckboxChange(checkbox)
            }
        })
    }

    static onCheckboxChange (checkbox) {
        if (checkbox.checked) {
            if (this.selected.size < this.limit) {
                this.selected.add(checkbox.value)
            } else {
                checkbox.checked = false
            }
        } else {
            this.selected.delete(checkbox.value)
        }
        this.saveState()
        this.update()
    }

    static syncCheckboxesFromState () {
        this.checkboxes.forEach(checkbox => {
            checkbox.checked = this.selected.has(checkbox.value)
        })
    }

    static emptyForm () {
        this.inputs.innerHTML = ''
    }

    static addInputs () {
        this.selected.forEach(value => {
            this.inputs.innerHTML += `<input type="hidden" name="products[]" value="${value}">`
        })
    }

    static togglePopup () {
        this.popup.toggleAttribute('data-active', this.checkboxes.length > 0 && this.selected.size > 0)
    }

    static popupMessage () {
        const atLimit = this.selected.size === this.limit
        this.popup.querySelector('[data-compare-message-limit]').classList.toggle('hidden', atLimit)
        this.popup.querySelector('[data-compare-message-full]').classList.toggle('hidden', !atLimit)
    }

    static disableCheckboxes () {
        const atLimit = this.selected.size === this.limit
        this.checkboxes.forEach(checkbox => {
            const label = checkbox.closest('label')
            const shouldDisable = atLimit && !checkbox.checked
            checkbox.toggleAttribute('disabled', shouldDisable)
            if (label) label.toggleAttribute('disabled', shouldDisable)
        })
    }

    static disableSubmitButton () {
        const btn = this.popup.querySelector('[data-compare-submit-button]')
        btn.toggleAttribute('disabled', this.selected.size <= 1)
    }

    static addMediaQueryListeners () {
        this.mq.onchange = (event) => {
            this.limit = event.matches ? 3 : 2
            if (this.selected.size > this.limit) {
                this.selected = new Set([...this.selected].slice(0, this.limit))
            }
            this.update()
        }
    }

    static addLivewireListeners () {
        document.addEventListener('livewire:navigated', () => {
            this.checkCategoryReset()
            this.update()
        })
        document.addEventListener('livewire:update', () => {
            this.update()
        })
        document.addEventListener('livewire:init', () => {
            window.Livewire.hook('morphed', () => {
                this.update()
            })
        })
    }

    static getCurrentCategory () {
        return document.querySelector('[data-compare-category]')?.dataset.compareCategory ?? null
    }

    static checkCategoryReset () {
        const currentCategory = this.getCurrentCategory()
        if (currentCategory === null) return

        const storedCategory = sessionStorage.getItem(CATEGORY_KEY)
        if (storedCategory !== null && storedCategory !== currentCategory) {
            this.selected.clear()
            this.saveState()
        }
        sessionStorage.setItem(CATEGORY_KEY, currentCategory)
    }

    static reset () {
        this.selected.clear()
        this.saveState()
        this.update()
    }

    static initComparePage () {
        // Seed state from the compare page URL so the popup is correct when navigating back
        const urlProducts = new URLSearchParams(window.location.search).getAll('products[]')
        if (urlProducts.length) {
            urlProducts.forEach(id => this.selected.add(id))
            this.saveState()
        }

        document.querySelectorAll('[data-compare-toggle-all]').forEach(button => {
            button.addEventListener('click', () => {
                const isOpen = ariaIs('expanded', button)
                ariaSet('expanded', button, isOpen ? 'false' : 'true')
                button.closest('section').querySelectorAll('details').forEach(details => {
                    details.open = !isOpen
                })
            })
        })

        document.querySelectorAll('[data-compare-remove]').forEach(button => {
            button.addEventListener('click', () => {
                const idToRemove = button.dataset.compareRemove
                this.selected.delete(idToRemove)
                this.saveState()

                const params = new URLSearchParams(window.location.search)
                const remaining = params.getAll('products[]').filter(id => id !== idToRemove)
                params.delete('products[]')
                remaining.forEach(id => params.append('products[]', id))
                window.location.search = params.toString()
            })
        })
    }

    static saveState () {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...this.selected]))
    }

    static restoreState () {
        try {
            const stored = sessionStorage.getItem(STORAGE_KEY)
            if (stored) {
                this.selected = new Set(JSON.parse(stored))
            }
        } catch {
            this.selected = new Set()
        }
    }
}

const compare = (function () {
    const compare = ProductCompare
    return {
        init: () => compare.init(),
        reset: () => compare.reset()
    }
})()

export default compare
